import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "form",
    "doing",
    "wentWrong",
    "screenshotsPreview",
    "submitBtn",
    "loadingIndicator"
  ]

  screenshots = []

  async open(event) {
    event.preventDefault()
    this.showModal()
    this.setLoadingState(true)

    try {
      const screenshot = await this.captureScreenshot()
      this.addScreenshot(screenshot)
    } catch (error) {
      console.error("Screenshot capture failed:", error)
    } finally {
      this.setLoadingState(false)
    }

    this.populateHiddenFields()
  }

  close(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.add("hidden")
    this.resetForm()
  }

  async captureScreenshot() {
    const html2canvas = (await import("html2canvas")).default
    const topbarHeight = 74
    const sidebarWidth = 180
    const canvas = await html2canvas(document.body, {
      ignoreElements: (el) => el.id === "problem-report-modal",
      x: window.scrollX + sidebarWidth,
      y: window.scrollY + topbarHeight,
      width: window.innerWidth - sidebarWidth,
      height: window.innerHeight - topbarHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      backgroundColor: "#eaeaea"
    })
    return canvas.toDataURL("image/png")
  }

  addScreenshot(dataUrl) {
    this.screenshots.push(dataUrl)
    this.renderScreenshots()
  }

  removeScreenshot(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index)
    this.screenshots.splice(index, 1)
    this.renderScreenshots()
  }

  renderScreenshots() {
    this.screenshotsPreviewTarget.innerHTML = this.screenshots
      .map((dataUrl, i) => this.screenshotThumbnail(dataUrl, i))
      .join("")
  }

  screenshotThumbnail(dataUrl, index) {
    return `
      <div class="screenshot-thumb">
        <img src="${dataUrl}" />
        <button type="button"
                data-action="click->neversettle--problem-report#removeScreenshot"
                data-index="${index}">&times;</button>
      </div>
    `
  }

  async addMoreScreenshots(event) {
    const files = event.target.files
    for (const file of files) {
      const dataUrl = await this.fileToDataUrl(file)
      this.addScreenshot(dataUrl)
    }
    event.target.value = ""
  }

  fileToDataUrl(file) {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.readAsDataURL(file)
    })
  }

  async submit(event) {
    event.preventDefault()

    if (!this.validate()) return

    this.setSubmitting(true)

    try {
      const formData = await this.buildFormData()
      const response = await this.postForm(formData)
      this.handleResponse(response)
    } catch (error) {
      this.showErrors(["Network error. Please try again."])
    } finally {
      this.setSubmitting(false)
    }
  }

  async buildFormData() {
    const formData = new FormData()
    formData.append("problem[url]", this.urlValue())
    formData.append("problem[doing]", this.doingTarget.value)
    formData.append("problem[went_wrong]", this.wentWrongTarget.value)
    formData.append("problem[user_agent]", this.userAgentValue())

    for (let i = 0; i < this.screenshots.length; i++) {
      const blob = await this.dataUrlToBlob(this.screenshots[i])
      formData.append("problem[screenshots][]", blob, `screenshot-${i}.png`)
    }

    return formData
  }

  postForm(formData) {
    return fetch("/problems", {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": this.csrfToken()
      }
    })
  }

  async handleResponse(response) {
    if (response.ok) {
      this.showSuccess()
    } else {
      const data = await response.json()
      this.showErrors(data.errors)
    }
  }

  showSuccess() {
    this.formTarget.innerHTML = `
      <div class="success-message">
        <p>Thank you! Your report has been sent.</p>
        <button type="button" class="button" data-action="click->neversettle--problem-report#close">Close</button>
      </div>
    `
  }

  dataUrlToBlob(dataUrl) {
    return fetch(dataUrl).then((r) => r.blob())
  }

  validate() {
    const errors = []
    if (!this.doingTarget.value.trim()) {
      errors.push("Please describe what you were doing")
    }
    if (!this.wentWrongTarget.value.trim()) {
      errors.push("Please describe what went wrong")
    }

    if (errors.length) {
      this.showErrors(errors)
      return false
    }
    return true
  }

  showModal() {
    this.modalTarget.classList.remove("hidden")
  }

  setLoadingState(loading) {
    if (loading) {
      this.loadingIndicatorTarget.classList.remove("hidden")
    } else {
      this.loadingIndicatorTarget.classList.add("hidden")
    }
  }

  setSubmitting(submitting) {
    this.submitBtnTarget.disabled = submitting
    this.submitBtnTarget.textContent = submitting ? "Sending..." : "Send Report"
  }

  populateHiddenFields() {
    this.formTarget.querySelector('[name="problem[url]"]').value = this.urlValue()
    this.formTarget.querySelector('[name="problem[user_agent]"]').value = this.userAgentValue()
  }

  urlValue() {
    return window.location.href
  }

  userAgentValue() {
    return navigator.userAgent
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }

  showErrors(errors) {
    const el = this.formTarget.querySelector(".errors")
    el.innerHTML = errors.map((e) => `<p>${e}</p>`).join("")
  }

  resetForm() {
    this.screenshots = []
    this.formTarget.reset()
    this.screenshotsPreviewTarget.innerHTML = ""
    this.formTarget.querySelector(".errors").innerHTML = ""
  }
}
